import { Controller, useFormContext } from 'react-hook-form';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { isValid, isToday } from 'date-fns';

interface IDatePickerProps {
  name: string;
  label: string;
  minDate?: Date | null;
  proxy?: Boolean;
}

export function CalendarDatePicker(props: IDatePickerProps) {
  const { name, label, minDate, proxy } = props;
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const valid = (date: Date | null) => {
    if (date == null) {
      return true;
    }
    return isValid(date);
  };

  /**
   * Checks whether the selected date is less than the minimum date if supplied
   * @param date the selected date
   * @returns true if date is valid, else false
   */
  const validate = (date: Date | null) => {
    let today = new Date();

    if (proxy) {
      if (date == null) {
        return true;
      }
      if (isToday(date)) {
        return true;
      } else if (date > today) {
        return true;
      } else if (date < today) {
        return false;
      }
    } else {
      const isValidMinDate = isValid(minDate);

      if (minDate && isValidMinDate && date !== null) {
        return date >= minDate;
      }

      return true;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Controller
        control={control}
        name={name}
        rules={{
          validate: {
            isValid: date => valid(date) || 'Invalid date',
            validate: date => validate(date) || 'End date cannot be set before todays date.',
          },
        }}
        render={({ field }) => (
          <DatePicker
            {...field}
            label={label}
            inputFormat="dd/MM/yyyy"
            value={field.value}
            onChange={e => field.onChange(e)}
            minDate={minDate}
            renderInput={params => (
              <TextField
                {...(params as TextFieldProps)}
                error={Boolean(errors[name])}
                helperText={errors[name]?.message}
                fullWidth
              />
            )}
          />
        )}
      />
    </LocalizationProvider>
  );
}
